import { Box, styled, Typography, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import { useCustomTheme } from '../store/theme-context';
import { debugLog } from '../utils/debug';

type Props = {
	content: any;
	animationId?: string;
};
const FullWidthText = ({ content, animationId }: Props) => {
	const { breakpoints, palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const text = content.text ? content.text : '';
	const animationDuration = content.animation_duration || 20; // domyślnie 20s

	// Obsługa use_easing z ACF - może być boolean, string "1"/"0", lub undefined
	// ACF True/False może zwracać: true/false, 1/0, "1"/"0", lub undefined
	const useEasingRaw = content.use_easing;
	const useEasing =
		useEasingRaw === undefined
			? true // domyślnie true jeśli nie ustawione
			: useEasingRaw === true || useEasingRaw === 1 || useEasingRaw === '1' || useEasingRaw === 'true';

	// Debug - można usunąć po weryfikacji
	debugLog('[FullWidthText] animationId:', animationId, 'animationDuration:', animationDuration);
	debugLog('[FullWidthText] use_easing value:', useEasingRaw, 'type:', typeof useEasingRaw, 'parsed:', useEasing);

	// Unikalna nazwa animacji dla każdej instancji, aby uniknąć synchronizacji
	const uniqueId = animationId || `full-width-text-${Math.random().toString(36).slice(2, 11)}`;

	// Animacja Test 8b: cubic-bezier(0.2, 0, 0.8, 1) - przetestowana i skalibrowana
	// 0-20%: hamowanie przy wjeździe
	// 20-80%: wolny przejazd przez środek (tekst w pełni widoczny)
	// 80-100%: przyspieszenie przy wyjeździe
	const slideWithEasing = keyframes`
      0% {
        transform: translateY(-50%) translateX(100vw);
      }
      20% {
        /* max(0, ...) zapobiega ujemnym wartościom gdy tekst jest węższy niż ekran */
        transform: translateY(-50%) translateX(max(0px, calc(50vw - 50%)));
      }
      80% {
        transform: translateY(-50%) translateX(min(0px, calc(100vw - 100%)));
      }
      100% {
        transform: translateY(-50%) translateX(-100%);
      }
    `;

	// Animacja liniowa (Test 4) - jako fallback gdy use_easing = false
	const slideLinear = keyframes`
      0% {
        transform: translateY(-50%) translateX(100vw);
      }
      100% {
        transform: translateY(-50%) translateX(-100%);
      }
    `;

	return (
		<Box
			overflow={'hidden'}
			component={'section'}
			sx={{
				backgroundColor: isHighContrast
					? palette.primary.light
					: content.background_color
						? content.background_color
						: palette.secondary.light,
				height: '216px',
				display: 'flex',
				alignItems: 'center',
				position: 'relative',
				py: '74px',
				[breakpoints.down('md')]: {
					py: '56px',
				},
			}}
		>
			<Box
				display={'flex'}
				alignItems={'center'}
				sx={{
					position: 'absolute',
					top: '50%',
					transform: 'translateY(-50%)',
					// Używamy unikalnego obiektu keyframes dla każdej instancji
					// emotion/react automatycznie generuje unikalną nazwę CSS dla każdego keyframes
					animation: `${useEasing ? slideWithEasing : slideLinear} ${animationDuration}s ${useEasing ? 'cubic-bezier(0.2, 0, 0.8, 1)' : 'linear'} infinite`,
				}}
			>
				<Typography
					component={'span'}
					variant={'h1'}
					sx={{
						display: 'inline-block',
						color: isHighContrast ? palette.primary.main : content.font_color && content.font_color,
						fontWeight: 500,
						whiteSpace: 'nowrap',
					}}
				>
					{text}
				</Typography>
			</Box>
		</Box>
	);
};

export default FullWidthText;
